import gsap from 'gsap'

import {
  fadeRevealAnimation,
  getScrollTriggerOptions,
  linesRevealAnimation,
  type SharedAnimation
} from '@/components/sections/animations/shared'

import { Animation } from '@/types'

export const manifestoRevealAnimation = ({
  node,
  context,
  reverse,
  delay = 0
}: Animation) => {
  const selector = context?.selector || gsap.utils.selector(node)

  const line1 = selector('[data-motion-line-1]')[0]
  const line2 = selector('[data-motion-line-2]')[0]
  const text = selector('[data-motion-text]')[0]
  const image = selector('[data-motion-image]')[0]
  const cta = selector('[data-motion-cta]')[0]

  // 1) Lines movement — scrubbed across the full component scroll
  const moveTimeline = gsap.timeline({
    scrollTrigger: {
      trigger: node,
      start: 'top bottom',
      end: 'bottom top',
      scrub: true,
      invalidateOnRefresh: true
    }
  })

  if (line1) {
    moveTimeline.fromTo(
      line1,
      { xPercent: 0 },
      { xPercent: -40, ease: 'none' },
      0
    )
  }

  if (line2) {
    moveTimeline.fromTo(
      line2,
      { xPercent: 0 },
      { xPercent: 40, ease: 'none' },
      0
    )
  }

  // 2) Image reveal — once, triggered by the image entering the viewport
  let imageTimeline: gsap.core.Timeline
  if (image) {
    const imageScrollOpts = getScrollTriggerOptions(image, false)
    imageTimeline = gsap
      .timeline(imageScrollOpts)
      .fromTo(
        image,
        { yPercent: 100 },
        { yPercent: 0, duration: 1.2, ease: 'power2.out' },
        0
      )
  }

  const revealScrollOpts = getScrollTriggerOptions(text, reverse)
  const revealTimeline = gsap.timeline({
    ...revealScrollOpts,
    delay
  })

  // 3) Text reveal — triggered by the text element itself entering the viewport
  let textAnimation: SharedAnimation
  if (text) {
    textAnimation = linesRevealAnimation(text, reverse)
    revealTimeline.add(textAnimation.timeline, 0)
  }

  let ctaAnimation: SharedAnimation
  if (cta) {
    ctaAnimation = fadeRevealAnimation(cta)
    revealTimeline.add(ctaAnimation.timeline, 0.6)
  }

  return () => {
    moveTimeline.scrollTrigger?.kill()
    moveTimeline.kill()
    imageTimeline?.scrollTrigger?.kill()
    imageTimeline?.kill()
    textAnimation?.revert()
    ctaAnimation?.revert()
  }
}
